/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from '../FileTables/FileTable.style';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import TenderRegistrationPublic from '~src/assets/nav-icons/file-tender-public.svg';
import TenderRegistrationPublicRevoked from '~src/assets/nav-icons/file-tender-public-revoked.svg';
import CachedPublicTenderContext from '~src/contexts/CachedPublicTender';
import {
  PublicTenderTableEntry,
  usePublicTendersSimultaneously,
} from '~src/hooks/api-hooks/usePublicTendersSimultaneously';
import { formatDate } from '~src/utils/formatDate';

export interface CachedPublicTendersTableProps
  extends TableProps<PublicTenderTableEntry> {
  selectedKeys?: TableRowSelection<PublicTenderTableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<PublicTenderTableEntry>['onSelect'];
  // @ts-ignore
  onSelectAll?: TableRowSelection<PublicTenderTableEntry['onSelectAll']>;
}

export const CachedPublicTendersTable: React.FC<CachedPublicTendersTableProps> = ({
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();

  const {
    cachedPublicTendersContext,
    setCachedPublicTendersContext,
    setCurrentCachedPublicTender,
  } = useContext(CachedPublicTenderContext);

  const { publicTendersList } = usePublicTendersSimultaneously(
    cachedPublicTendersContext
  );

  useEffect(() => {
    if (cachedPublicTendersContext.length > 0) return;

    const storedPublicTenders = localStorage.getItem('publicTenders');

    if (!storedPublicTenders) return;

    try {
      setCachedPublicTendersContext(JSON.parse(storedPublicTenders));
    } catch {
      localStorage.removeItem('publicTenders');
    }
  }, [cachedPublicTendersContext.length, setCachedPublicTendersContext]);

  const getTitle = (record: PublicTenderTableEntry) =>
    record.share_title || record.title || record.tenderShareId;

  const getEffectiveDeadline = (record: PublicTenderTableEntry) => {
    const participationDeadline = Number(record.active_until) || 0;
    const mainDeadline = Number(record.deadline_at) || 0;

    const isParticipationDeadlineValid = participationDeadline > 3601;
    const isMainDeadlineValid = mainDeadline > 3601;

    if (isParticipationDeadlineValid && isMainDeadlineValid) {
      return participationDeadline < mainDeadline
        ? participationDeadline
        : mainDeadline;
    }

    if (isMainDeadlineValid) return mainDeadline;
    if (isParticipationDeadlineValid) return participationDeadline;

    return 0;
  };

  const getTenderStateLabel = (state?: number): string => {
    switch (state) {
      case 1:
        return intl.formatMessage({
          id: 'tender_state_draft',
        });
      case 3:
        return intl.formatMessage({
          id: 'tender_state_active',
        });
      case 6:
        return intl.formatMessage({
          id: 'tender_state_closed',
        });
      case 7:
        return intl.formatMessage({
          id: 'tender_state_processed',
        });
      default:
        return intl.formatMessage({
          id: 'tender_state_unknown',
        });
    }
  };

  const isPublicTenderUnavailable = (record: PublicTenderTableEntry) =>
    record.publicTenderRequestStatus === 'unavailable' ||
    Number(record.publicTenderHttpCode) >= 400;

  const getPublicTenderStatusLabel = (record: PublicTenderTableEntry) => {
    if (isPublicTenderUnavailable(record)) {
      if (record.publicTenderHttpCode === 403) {
        return intl.formatMessage({ id: 'key-not-valid' });
      }

      if (record.publicTenderHttpCode === 404) {
        return intl.formatMessage({ id: 'public-tender-not-found' });
      }

      return intl.formatMessage({ id: 'tender_state_unknown' });
    }

    return getTenderStateLabel(Number(record.state));
  };

  const getPublicTenderStatusColor = (record: PublicTenderTableEntry) => {
    if (isPublicTenderUnavailable(record)) return '#ff4d4f';

    switch (Number(record.state)) {
      case 1:
        return '#1890ff';
      case 3:
        return '#52c41a';
      case 6:
        return '#fa8c16';
      case 7:
        return '#722ed1';
      default:
        return '#8c8c8c';
    }
  };

  const columns = useMemo<TableProps<PublicTenderTableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 50,
        align: 'center',
        render: (_type, record) => {
          const unavailable = isPublicTenderUnavailable(record);

          return {
            children: (
              <div style={{ marginLeft: '10px', width: 30, height: 30 }}>
                <NavIcon
                  icon={
                    unavailable
                      ? TenderRegistrationPublicRevoked
                      : TenderRegistrationPublic
                  }
                />
              </div>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'status' }),
        key: 'public_tender_status',
        width: 140,
        sorter: (a, b) =>
          getPublicTenderStatusLabel(a).localeCompare(
            getPublicTenderStatusLabel(b)
          ),
        render: (_, record) => (
          <span
            style={{
              color: getPublicTenderStatusColor(record),
              fontWeight: 500,
            }}>
            {getPublicTenderStatusLabel(record)}
          </span>
        ),
      },
      {
        title: intl.formatMessage({ id: 'title' }),
        dataIndex: 'title',
        sorter: (a, b) => getTitle(a).localeCompare(getTitle(b)),
        render: (_title, record) => <Fragment>{getTitle(record)}</Fragment>,
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        ellipsis: true,
        render: (description) => description || '-',
      },
      {
        title: intl.formatMessage({ id: 'deadline' }),
        key: 'calculated_deadline',
        sorter: (a, b) => getEffectiveDeadline(a) - getEffectiveDeadline(b),
        width: 180,
        ellipsis: true,
        render: (_, record) => {
          const finalTimestamp = getEffectiveDeadline(record);

          if (finalTimestamp <= 3601) {
            return '-';
          }

          return <div>{formatDate(finalTimestamp, 'short')}</div>;
        },
      },
      {
        title: intl.formatMessage({ id: 'gs-short' }),
        dataIndex: 'global_serial',
        width: 100,
        ellipsis: true,
        render: (globalSerial) => globalSerial || '-',
        sorter: (a, b) =>
          (a.global_serial || '').localeCompare(b.global_serial || ''),
      },
    ],
    [intl]
  );

  return (
    <Table<PublicTenderTableEntry>
      rowKey="tenderShareId"
      columns={columns}
      dataSource={publicTendersList}
      onRow={(record) => ({
        onClick: () => {
          setCurrentCachedPublicTender({
            tenderShareId: record.tenderShareId,
          });

          history.push(`/public-tenders/${record.tenderShareId}/details`);
        },
      })}
      pagination={false}
      bordered
      showSorterTooltip={false}
      css={[
        styles.table,
        { marginTop: '20px' },
        tableProps.scroll?.y && {
          '.ant-table-body': {
            height: tableProps.scroll.y,
          },
        },
      ]}
      className="main-content-window"
      {...tableProps}
    />
  );
};
